// UserSelection.tsx - Enhanced with Modal Focus Integration
import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import './FunctionsManagement.css'; 
import { User } from './types';

interface UserSelectionProps {
  users: User[];
  selectedUserIds: number[];
  onSelectionChange: (selectedIds: number[]) => void;
  label: string;
  placeholder?: string;
  isLoading?: boolean;
  disabled?: boolean;
  onDropdownClose?: () => void; // New prop for modal focus management
}

export const UserSelection: React.FC<UserSelectionProps> = ({
  users,
  selectedUserIds,
  onSelectionChange,
  label,
  placeholder = "Search users by name or email...",
  isLoading = false,
  disabled = false,
  onDropdownClose // New prop
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // Refs for focus management
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) {
      return users;
    }

    const term = searchTerm.toLowerCase().trim();
    
    return users.filter(user => 
      user.userName.toLowerCase().includes(term) ||
      user.userEmail.toLowerCase().includes(term) ||
      (user.role && user.role.toLowerCase().includes(term))
    );
  }, [users, searchTerm]);

  // Get selected users details
  const selectedUsers = useMemo(() => {
    return users.filter(user => selectedUserIds.includes(user.userId));
  }, [users, selectedUserIds]);

  // Handle user selection/deselection with focus management
  const handleUserToggle = useCallback((userId: number, event?: React.MouseEvent) => {
    // Prevent event bubbling
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (selectedUserIds.includes(userId)) {
      onSelectionChange(selectedUserIds.filter(id => id !== userId));
    } else {
      onSelectionChange([...selectedUserIds, userId]);
    }

    setSearchTerm('');
    setIsDropdownOpen(false);
    searchInputRef.current?.blur();
    
  }, [selectedUserIds, onSelectionChange]);

  // Remove user from selection with focus management
  const handleRemoveUser = useCallback((userId: number, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    onSelectionChange(selectedUserIds.filter(id => id !== userId));
  }, [selectedUserIds, onSelectionChange]);

  // Clear all selections
  const handleClearAll = useCallback((event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    onSelectionChange([]);
  }, [onSelectionChange]);

  // Handle dropdown open
  const handleDropdownOpen = useCallback((event?: React.FocusEvent | React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    setIsDropdownOpen(true);
    
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 50);
  }, []);

  // Enhanced dropdown close with modal focus management
  const handleDropdownClose = useCallback(() => {
    setIsDropdownOpen(false);
    setSearchTerm(''); // Clear search when closing
    
    // Notify parent modal to recapture focus
    if (onDropdownClose) {
      setTimeout(() => {
        onDropdownClose();
      }, 100);
    }
  }, [onDropdownClose]);

  // Handle search input change
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    setSearchTerm(event.target.value);
  }, []);

  // Handle search clear
  const handleSearchClear = useCallback((event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setSearchTerm('');
    
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 10);
  }, []);

  // Handle dropdown overlay click
  const handleDropdownOverlayClick = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    handleDropdownClose();
  }, [handleDropdownClose]);

  // Handle dropdown content click (prevent bubbling)
  const handleDropdownContentClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  // Enhanced keyboard navigation
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    // Always stop propagation to prevent modal issues
    event.stopPropagation();
    
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        handleDropdownClose();
        break;
      case 'Tab':
        // Allow tab but close dropdown when tabbing away from this component
        setTimeout(() => {
          if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
            handleDropdownClose();
          }
        }, 10);
        break;
      case 'Enter':
        if (!isDropdownOpen) {
          event.preventDefault();
          handleDropdownOpen();
        }
        break;
      default:
        // Other keys are fine, just prevent propagation
        break;
    }
  }, [handleDropdownClose, handleDropdownOpen, isDropdownOpen]);

  // Handle input blur with delay to allow for clicks
  const handleInputBlur = useCallback((event: React.FocusEvent) => {
    // Don't close immediately, allow time for dropdown clicks
    setTimeout(() => {
      if (dropdownRef.current && !dropdownRef.current.contains(document.activeElement)) {
        handleDropdownClose();
      }
    }, 150);
  }, [handleDropdownClose]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        handleDropdownClose();
      }
    };

    if (isDropdownOpen) {
      // Small delay to allow current click to complete
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 10);
      
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen, handleDropdownClose]);

  if (isLoading) {
    return (
      <div className="form-group">
        <label className="form-label">{label}</label>
        <div className="user-selection-loading">
          <span className="spinner"></span>
          Loading users...
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="form-group">
      <label className="form-label">
        {label}
        {selectedUsers.length > 0 && (
          <span className="selection-count"> ({selectedUsers.length} selected)</span>
        )}
      </label>

      {/* Selected Users Display */}
      {selectedUsers.length > 0 && (
        <div className="selected-users">
          <div className="selected-users-header">
            <span>Selected {label.toLowerCase()}:</span>
            <button
  type="button"
  onClick={handleClearAll}
  className="clear-all-btn"
  disabled={disabled}
  title="Clear all selected users" // Tooltip for accessibility
  aria-label="Clear all selected users" // Screen reader accessibility
>
  🗑️
</button>
          </div>
          <div className="selected-users-list">
            {selectedUsers.map(user => (
              <div key={user.userId} className="selected-user-tag">
                <div className="user-info">
                  <strong>{user.userName}</strong>
                  <small>{user.userEmail}</small>
                  
                </div>
                <button
                  type="button"
                  onClick={(e) => handleRemoveUser(user.userId, e)}
                  className="remove-user-btn"
                  title="Remove user"
                  disabled={disabled}
                  aria-label={`Remove ${user.userName}`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User Search and Selection */}
      <div ref={dropdownRef} className="user-selection-dropdown">
        <div className="search-input-container">
          <input
            ref={searchInputRef}
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={handleDropdownOpen}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            className="form-input user-search-input"
            disabled={disabled}
          />
          <span className="search-icon">🔍</span>
          {searchTerm && (
            <button
              type="button"
              onClick={handleSearchClear}
              className="clear-search-btn"
              title="Clear search"
              onMouseDown={(e) => e.preventDefault()} // Prevent blur before click
            >
              ✕
            </button>
          )}
        </div>

        {isDropdownOpen && (
          <>
            {/* Dropdown Overlay */}
            <div 
              className="dropdown-overlay" 
              onClick={handleDropdownOverlayClick}
            />
            
            {/* Dropdown Content */}
            <div 
              className="user-dropdown"
              onClick={handleDropdownContentClick}
              onMouseDown={(e) => e.preventDefault()} // Prevent input blur
            >
              <div className="dropdown-header">
                <span>Available Users ({filteredUsers.length})</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDropdownClose();
                  }}
                  className="close-dropdown-btn"
                  aria-label="Close dropdown"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  ✕
                </button>
              </div>
              
              <div className="user-list">
                {filteredUsers.length === 0 ? (
                  <div className="no-users-found">
                    {searchTerm ? `No users found matching "${searchTerm}"` : 'No users available'}
                  </div>
                ) : (
                  filteredUsers.map(user => {
                    const isSelected = selectedUserIds.includes(user.userId);
                    return (
                      <div
                        key={user.userId}
                        className={`user-option ${isSelected ? 'selected' : ''}`}
                        onClick={(e) => !disabled && handleUserToggle(user.userId, e)}
                        onMouseDown={(e) => e.preventDefault()} // Prevent input blur
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!disabled) {
                              handleUserToggle(user.userId);
                            }
                          }
                        }}
                        tabIndex={0}
                        role="option"
                        aria-selected={isSelected}
                      >
                        <div className="user-checkbox">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}} // Controlled by parent onClick
                            disabled={disabled}
                            tabIndex={-1}
                            aria-hidden="true"
                          />
                        </div>
                        <div className="user-details">
                          <div className="user-name">{user.userName}</div>
                          <div className="user-email">{user.userEmail}</div>
                         
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <small className="form-help">
        Search and select multiple users by clicking on them. Selected users will appear above.
      </small>
    </div>
  );
};